import { Link, Navigate } from 'react-router-dom'
import { ArrowDown, ArrowUpRight } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import Footer from '@/components/Footer'

/**
 * Public landing page — the front of the book. Anyone on the internet
 * can see this. Authenticated members are routed straight to /dashboard.
 *
 * Aesthetic: editorial / lakeside / heritage. Big serif typography,
 * generous whitespace, asymmetric layouts, atmospheric darks against
 * cream paper. No SaaS feature grids, no purple gradients, no AI slop.
 */
const LandingPage = () => {
  const { status, isLoading } = useAuthStore()
  if (status === 'authenticated' && !isLoading) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="bg-paper text-deepwater min-h-screen overflow-x-hidden">
      <PublicHeader />
      <Hero />
      <YearMarker />
      <InsideTheBook />
      <Footer />
    </div>
  )
}

// ---------- Header ----------------------------------------------------------

const PublicHeader = () => (
  <header className="absolute top-0 inset-x-0 z-30 px-6 lg:px-10 pt-6 pb-4 flex items-center">
    <div className="flex items-center gap-3 text-white">
      <span className="w-10 h-10 rounded-full border border-white/40 flex items-center justify-center font-serif italic text-base">
        S
      </span>
      <div className="leading-tight hidden sm:block">
        <div className="font-serif text-base tracking-wide">Shorewood Association</div>
        <div className="text-[0.6rem] uppercase tracking-[0.3em] text-white/60">
          Lake Michigan · est. 1902
        </div>
      </div>
    </div>
  </header>
)

// ---------- Hero ------------------------------------------------------------

const Hero = () => (
  <section className="relative min-h-screen flex flex-col items-center justify-center text-white overflow-hidden">
    {/* Background image */}
    <div
      className="absolute inset-0 bg-deepwater"
      style={{
        backgroundImage: 'url(/fonts/images/login_background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      aria-hidden="true"
    />
    {/* Gradient veils — push the image back, lift the type forward */}
    <div
      className="absolute inset-0 bg-gradient-to-b from-deepwater/85 via-deepwater/55 to-deepwater/95"
      aria-hidden="true"
    />
    <div
      className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(201,160,82,0.10),transparent_55%)]"
      aria-hidden="true"
    />
    {/* Hand-drawn deckled hairline at the bottom — a small editorial flourish */}
    <div
      className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
      aria-hidden="true"
    />

    {/* Content */}
    <div className="relative z-10 px-6 max-w-6xl w-full text-center">
      <p
        className="text-[0.65rem] sm:text-[0.7rem] uppercase tracking-[0.45em] text-gold/85 mb-8 animate-fade-in"
        style={{ animationDelay: '120ms', animationFillMode: 'both' }}
      >
        A private association on the lake
      </p>

      <h1
        className="font-serif font-bold leading-[0.88] tracking-tight text-balance animate-fade-in"
        style={{
          fontSize: 'clamp(4.5rem, 16vw, 14rem)',
          animationDelay: '240ms',
          animationFillMode: 'both',
        }}
      >
        Shorewood.
      </h1>

      <p
        className="mt-10 font-serif italic text-xl sm:text-2xl text-white/80 max-w-2xl mx-auto leading-relaxed text-balance animate-fade-in"
        style={{ animationDelay: '420ms', animationFillMode: 'both' }}
      >
        Twenty-six cottages, one lake, six generations&nbsp;of summers.
      </p>

      <div
        className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-fade-in"
        style={{ animationDelay: '600ms', animationFillMode: 'both' }}
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-deepwater font-semibold hover:bg-gold hover:text-white transition-colors shadow-lift"
        >
          Sign in
        </Link>
        <Link
          to="/signup"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
        >
          Request access
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>

    {/* Scroll cue */}
    <div
      className="absolute bottom-10 inset-x-0 flex flex-col items-center text-white/60 text-[0.55rem] uppercase tracking-[0.4em] gap-2 animate-fade-in"
      style={{ animationDelay: '900ms', animationFillMode: 'both' }}
    >
      <span>scroll</span>
      <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
    </div>
  </section>
)

// ---------- 1902 marker -----------------------------------------------------

const YearMarker = () => (
  <section className="relative bg-paper py-28 sm:py-36 px-6 overflow-hidden">
    {/* Subtle vertical hairline grid — like notebook ruling */}
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
        <div className="lg:col-span-7 relative">
          <span className="block text-[0.65rem] uppercase tracking-[0.45em] text-copper mb-4">
            est. nineteen-oh-two
          </span>
          <p
            className="font-serif font-bold text-deepwater leading-[0.82] tracking-tight"
            style={{ fontSize: 'clamp(7rem, 19vw, 19rem)' }}
          >
            1902
          </p>
        </div>
        <div className="lg:col-span-5">
          <p className="font-serif italic text-2xl text-deepwater leading-snug text-pretty mb-6">
            A few cottages, a stretch of shore, and the&nbsp;unspoken
            agreement that the place would be tended.
          </p>
          <p className="text-base text-slate leading-relaxed text-pretty">
            Six generations in, the chapel still opens Memorial Day weekend,
            the picnic still ends at the beach, and the screen doors still
            slam the way they always have. We are the fortunate few —
            blessed with a place where it's strongly encouraged to slow
            down the pace.
          </p>
        </div>
      </div>

      {/* Ornament — three diamonds over a hairline */}
      <div className="mt-20 flex items-center justify-center gap-4 text-copper/60 text-xs">
        <span className="block flex-1 h-px bg-gradient-to-r from-transparent to-copper/30" />
        <span>◆ ◆ ◆</span>
        <span className="block flex-1 h-px bg-gradient-to-l from-transparent to-copper/30" />
      </div>
    </div>
  </section>
)

// ---------- What's inside ---------------------------------------------------

const insideItems = [
  'A complete cottage directory — every family, every number, every landline.',
  'The bulletin board — births, sailings, lost-and-found, what the deer ate this week.',
  'Bylaws, budgets, and meeting minutes back to the founding.',
  'The annual album — a hundred summers of photographs, year by year.',
  'Live lake conditions — water, wind, sunrise, and what the buoy is reading.',
  'A private inbox between members, so the directory is more than a list.',
]

const InsideTheBook = () => (
  <section className="relative bg-paper py-28 sm:py-36 px-6 overflow-hidden">
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left column — the framing */}
        <div className="lg:col-span-5">
          <p className="text-[0.65rem] uppercase tracking-[0.45em] text-copper mb-5">
            inside the portal
          </p>
          <h2
            className="font-serif font-bold text-deepwater leading-[0.94] tracking-tight mb-7"
            style={{ fontSize: 'clamp(2.5rem, 5.5vw, 4rem)' }}
          >
            What lives
            <br />
            inside the book.
          </h2>
          <p className="text-base text-slate leading-relaxed text-pretty mb-8">
            The portal is the working copy of the association — the directory,
            the bulletin, the annals, the budgets, the photographs. It opens
            only for members. Sign in if you have an account, or write to the
            board if you should be on the list.
          </p>
          <a
            href="mailto:board@shorewoodmi.com"
            className="inline-flex items-center gap-2 text-sm font-medium text-deepwater hover:text-copper transition-colors group"
          >
            <span className="underline decoration-copper/50 underline-offset-4 group-hover:decoration-copper">
              board@shorewoodmi.com
            </span>
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Right column — the index */}
        <div className="lg:col-span-7">
          <ol className="font-serif">
            {insideItems.map((item, i) => (
              <li
                key={i}
                className="group flex gap-6 sm:gap-8 py-5 border-b border-mist/40 first:border-t first:border-mist/40 first:pt-5"
              >
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-copper pt-2 shrink-0 w-8 select-none">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-lg sm:text-xl text-deepwater leading-snug text-pretty transition-colors group-hover:text-copper">
                  {item}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  </section>
)

export default LandingPage
